import { css } from 'aphrodite';
import { bs } from '../../../styles/styles';
import { styleSheet as automationStepCardStyles } from '../../../components/automation/AutomationStepCard/styles';
import { field, fieldReadOnly, placeholder, fieldWithMargin } from '../nodes/renderers/styles/commonRendererStyles';

/**
 * Props for the DisplayField component
 */
interface DisplayFieldProps {
	/**
	 * The label text to display above the field
	 */
	label: string;

	/**
	 * The value to display in the field
	 */
	value: string | number | null;

	/**
	 * Whether this field is required (shows asterisk indicator)
	 * @default false
	 */
	required?: boolean;

	/**
	 * Whether this field is in read-only mode (uses gray background)
	 * @default false
	 */
	readOnly?: boolean;

	/**
	 * Placeholder text to show when value is empty or null
	 */
	placeholder?: string;

	/**
	 * Additional CSS class names to apply to the field container
	 */
	className?: string;

	/**
	 * Whether to add bottom margin for spacing between fields
	 * @default false
	 */
	withMargin?: boolean;

	/**
	 * Custom styles to apply to the field value container
	 */
	fieldStyle?: React.CSSProperties;
}

/**
 * DisplayField component for rendering labeled, read-only display fields
 * in automation step renderers.
 *
 * This component provides a consistent visual appearance for displaying
 * field values in automation flows, with support for required indicators,
 * read-only styling, and placeholder text.
 *
 * @example
 * ```tsx
 * <DisplayField
 *   label="Tag name:"
 *   value={tagName}
 *   required
 *   readOnly
 *   placeholder="No tag selected"
 * />
 * ```
 */
export function DisplayField({
	label,
	value,
	required = false,
	readOnly = false,
	placeholder: placeholderText,
	className,
	withMargin = false,
	fieldStyle,
}: DisplayFieldProps) {
	const hasValue = value !== null && value !== undefined && value !== '';
	const displayValue = hasValue ? value : placeholderText || '';
	const isPlaceholder = !hasValue && !!placeholderText;

	return (
		<div className={className}>
			<div className={css(automationStepCardStyles.fieldLabel)}>
				{label} {required && <span className={css(bs.required)}>*</span>}
			</div>
			<div
				className={css(field, readOnly && fieldReadOnly, isPlaceholder && placeholder, withMargin && fieldWithMargin)}
				style={fieldStyle}
			>
				{displayValue}
			</div>
		</div>
	);
}
